import { useState, useEffect } from 'react';
import api from '../../utils/api';

const useDashboardData = () => {
  const [userFullName, setUserFullName] = useState("User");
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());

  const [stats, setStats] = useState([]);
  const [maintenanceByMonth, setMaintenanceByMonth] = useState(null);
  const [workOrderChartData, setWorkOrderChartData] = useState(null); 

  useEffect(() => {
    const fetchData = async () => {
      try {
        const date = new Date();
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth();

        const maintenanceRes = await api.get('/getWorkOrders');
        const maintenance = maintenanceRes.data || [];

        // Count total work orders
        const totalWorkOrders = maintenance.length;

        // Count current month work orders
        const currentMonthWorkOrders = maintenance.filter((m) => {
          const created = new Date(m.created_at);
          return created.getMonth() === currentMonth && created.getFullYear() === currentYear;
        }).length;

        // Count resolved this month
        const resolvedThisMonth = maintenance.filter((m) => {
          const resolved = new Date(m.resolved_at || m.updated_at);
          return m.status === 'Resolved' && resolved.getMonth() === currentMonth && resolved.getFullYear() === currentYear;
        }).length;

        // Calculate average resolution time (days)
        const resolutionTimes = maintenance
          .filter((m) => m.status === 'Resolved' && m.created_at && m.resolved_at)
          .map((m) => {
            const created = new Date(m.created_at);
            const resolved = new Date(m.resolved_at);
            return (resolved - created) / (1000 * 60 * 60 * 24); // days
          });

        const avgResolutionTime =
          resolutionTimes.length > 0
            ? (resolutionTimes.reduce((a, b) => a + b, 0) / resolutionTimes.length).toFixed(1)
            : 'N/A';

        // 🔁 Build dynamic Work Orders by Month Chart for all years
        const yearMap = {};
        maintenance.forEach((m) => {
          const createdDate = new Date(m.created_at);
          const year = createdDate.getFullYear();
          const month = createdDate.getMonth();

          if (!yearMap[year]) {
            yearMap[year] = Array(12).fill(0);
          }
          yearMap[year][month]++;
        });

        const colorPalette = [
          '#00d4ff', '#1e90ff', '#ff9800', '#8bc34a', '#f44336',
          '#9c27b0', '#ffc107', '#795548', '#607d8b', '#e91e63',
        ];

        const maintenanceByMonth = {
          labels: [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December',
          ],
          datasets: Object.entries(yearMap).map(([year, data], idx) => ({
            label: year,
            data,
            borderColor: colorPalette[idx % colorPalette.length],
            backgroundColor: `${colorPalette[idx % colorPalette.length]}40`,
            pointBackgroundColor: '#FFF',
            pointBorderColor: colorPalette[idx % colorPalette.length],
            tension: 0.4,
          })),
        };

        // Work Orders by Status
        const statusMap = {};
        maintenance.forEach((order) => {
          const status = order.status || 'Unknown';
          statusMap[status] = (statusMap[status] || 0) + 1;
        });

        const statusColors = {
          Open: '#03A9F4',
          'In Progress': '#FFC107',
          Hold: '#9C27B0',
          Resolved: '#4CAF50',
          Closed: '#607D8B',
          Unknown: '#9E9E9E',
        };

        const workOrderChartData = {
          labels: Object.keys(statusMap),
          datasets: [{
            label: 'Work Orders by Status',
            data: Object.values(statusMap),
            backgroundColor: Object.keys(statusMap).map((status) => statusColors[status] || '#ccc'),
            borderColor: Object.keys(statusMap).map((status) => statusColors[status] || '#ccc'),
            borderWidth: 1,
          }],
        };

        const stats = [
          {
            icon: '📦',
            title: 'Total Work Orders',
            count: totalWorkOrders,
            backgroundColor: '#2196F3',
          },
          {
            icon: '🗓️',
            title: 'This Month',
            count: currentMonthWorkOrders,
            backgroundColor: '#03A9F4',
          },
          {
            icon: '✅',
            title: 'Resolved This Month',
            count: resolvedThisMonth,
            backgroundColor: '#4CAF50',
          },
          {
            icon: '📈',
            title: 'Avg. Resolution Time',
            count: avgResolutionTime + ' days',
            backgroundColor: '#607D8B',
          },
        ];

        const firstName = localStorage.getItem('firstName') || 'Admin';
        const lastName = localStorage.getItem('lastName') || 'User';
        setUserFullName(`${firstName} ${lastName}`);

        setStats(stats);
        setMaintenanceByMonth(maintenanceByMonth);
        setWorkOrderChartData(workOrderChartData);
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
      }
    };

    fetchData();

    const interval = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return {
    userFullName,
    currentTime,
    stats,
    maintenanceByMonth,
    workOrderChartData,
  };
};

export default useDashboardData;
